import { B2B_FORM_VALIDATION } from '../constants';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateEmail = (email: string): ValidationResult => {
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }
  
  if (!B2B_FORM_VALIDATION.EMAIL_PATTERN.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  return { isValid: true };
};

export const validatePassword = (password: string): ValidationResult => {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }
  
  if (password.length < B2B_FORM_VALIDATION.PASSWORD_MIN_LENGTH) {
    return { isValid: false, error: `Password must be at least ${B2B_FORM_VALIDATION.PASSWORD_MIN_LENGTH} characters` };
  }
  
  // Check for complexity: uppercase, lowercase, number, special character
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
    return { 
      isValid: false, 
      error: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character' 
    };
  }
  
  return { isValid: true };
};

export const validateName = (name: string, fieldName: string = 'Name'): ValidationResult => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  if (name.length < B2B_FORM_VALIDATION.NAME_MIN_LENGTH) {
    return { isValid: false, error: `${fieldName} must be at least ${B2B_FORM_VALIDATION.NAME_MIN_LENGTH} characters` };
  }
  
  if (name.length > B2B_FORM_VALIDATION.NAME_MAX_LENGTH) {
    return { isValid: false, error: `${fieldName} must be less than ${B2B_FORM_VALIDATION.NAME_MAX_LENGTH} characters` };
  }
  
  return { isValid: true };
};

export const validatePhone = (phone: string): ValidationResult => {
  if (!phone) {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  if (!B2B_FORM_VALIDATION.PHONE_PATTERN.test(phone)) {
    return { isValid: false, error: 'Please enter a valid phone number' };
  }
  
  return { isValid: true };
};

export const validateUrl = (url: string): ValidationResult => {
  if (!url) {
    return { isValid: true }; // URL is optional
  }
  
  if (!B2B_FORM_VALIDATION.URL_PATTERN.test(url)) {
    return { isValid: false, error: 'Please enter a valid URL' };
  }
  
  return { isValid: true };
};

export const validateConfirmPassword = (password: string, confirmPassword: string): ValidationResult => {
  if (!confirmPassword) {
    return { isValid: false, error: 'Confirm password is required' };
  }
  
  if (password !== confirmPassword) {
    return { isValid: false, error: 'Passwords do not match' };
  }
  
  return { isValid: true };
};

// Utility function to validate multiple fields
export const validateForm = (fields: Record<string, any>, validators: Record<string, (value: any) => ValidationResult>): Record<string, string> => {
  const errors: Record<string, string> = {};
  
  Object.keys(validators).forEach(fieldName => {
    const validator = validators[fieldName];
    const value = fields[fieldName];
    const result = validator(value);
    
    if (!result.isValid && result.error) {
      errors[fieldName] = result.error;
    }
  });
  
  return errors;
};
